import { useTranslation } from 'next-i18next'

import { FormEvent, useRef } from 'react'

import { useRouter } from 'next/router'

import loginAnalytics from '~/analytics/login/loginAnalytics'
import OrLine from '~/components/OrLine'

import { LoginFields } from '~/auth/types/login.types'

import ButtonGoogleLogin from '~/auth/components/ButtonGoogleLogin'

import { useAppSelector, useAppDispatch } from '~/redux/hooks'

import { selectBackendError, selectFields, setFieldsActionCreator } from '~/auth/slices/login.slice'

import useAuthenticateWithEmail from '~/auth/hooks/useAuthenticateWithEmail'

import Button from '~/components/Buttons/Button'

import { MobileAppInput } from '~/components/MobileAppInput'

import ROUTES from '~/shared/constants/routes.const'

import { useEmailInputValidationLocale } from '../../hooks/useEmailInputValidationLocale'

import ButtonFacebookLogin from './../ButtonFacebookLogin'

import LoginServerError from './../LoginServerError'

export function AuthenticateV2(): React.ReactElement {
  const router = useRouter()
  const { t } = useTranslation(['logIn', 'authenticate', 'fields'])
  const dispatch = useAppDispatch()
  const fields = useAppSelector(selectFields)
  const backendError = useAppSelector(selectBackendError)
  const { handleAuthenticateWithEmail, isLoading } = useAuthenticateWithEmail()
  const ref = useRef<HTMLInputElement>(null)

  const handleOnSubmit = (evt: FormEvent): void => {
    loginAnalytics.logButtonClick('login')
    handleAuthenticateWithEmail(evt)
  }

  const handleOnChangeTextFields = (name: string, value: string): void => {
    // track only when the first character is placed
    if (fields[name as keyof LoginFields] === '' && value !== '') {
      loginAnalytics.writingInForms(name)
    }
    dispatch(setFieldsActionCreator({ ...fields, [name]: value }))
  }

  const handleOnClickForgetPassword = (): void => {
    loginAnalytics.logButtonClick('forget_password')
    router.push(ROUTES.RESET_PASSWORD)
  }

  useEmailInputValidationLocale(fields.email, ref)

  return (
    <div className="flex flex-col items-center justify-start p-7 w-full h-full max-w-470px">
      <div className="text-lg font-bold -mt-4s">{t('logIn:title')}</div>
      {backendError && (
        <div className="bg-blue-pastel/10 text-red w-full my-4 rounded-large border-1">
          <LoginServerError message={backendError} />
        </div>
      )}
      <form className="w-full flex gap-3 flex-col max-w-screen-19/20" onSubmit={handleOnSubmit}>
        <MobileAppInput
          autoComplete="email"
          inputClassName="rounded p-4 border border-white/10 border "
          label={t('fields:email')}
          name="email"
          placeholder="your@email.com"
          ref={ref}
          setValue={(val) => handleOnChangeTextFields('email', val)}
          type="email"
          value={fields.email}
        />

        <MobileAppInput
          autoComplete="password"
          inputClassName="rounded p-4 border border-white/10 border "
          label={t('fields:password')}
          name="password"
          placeholder="Your password"
          setValue={(val) => handleOnChangeTextFields('password', val)}
          type="password"
          value={fields.password}
        />
        <div className="flex justify-end mb-6">
          <p className="text-blue-pastel text-xxs cursor-pointer" onClick={handleOnClickForgetPassword}>
            {t('authenticate:forgotPassword')}
          </p>
        </div>

        <Button
          className="px-5 md:px-8 py-4 rounded-full bg-green-lime text-purple-darker !font-bold mx-auto tracking-tight md:tracking-normal"
          disabled={!fields.email || !fields.password}
          disableRing
          isFullWidth
          isLoading={isLoading}
          size="large"
          spinnerColor="black"
          type="submit"
        >
          {t('authenticate:logIn')}
        </Button>
      </form>
      <OrLine className="my-4" lineColor="bg-white" text={t('registerAccount:orContinue')} textColor="text-white" />

      <div className="flex flex-col w-9/10 md:w-10/12">
        <div className="flex gap-3 w-full">
          <ButtonGoogleLogin redirectBack />
          <ButtonFacebookLogin redirectBack />
        </div>
        <div className="text-center mt-4 bg-blue-pastel/10 rounded py-3 px-4 w-full">
          <p className="text-xxs mb-1">{t('authenticate:newToHom')}</p>
          <p className="text-purple-mist text-xxs">{t('authenticate:createAccountAtHom')}</p>
        </div>
      </div>
    </div>
  )
}
